import React, { FC, useContext } from 'react'
import { CropIcon, DropdownCaret } from '@components/icons'
import { Spinner } from '@components/spinner/Spinner'
import { observer } from 'mobx-react'
import { useTranslation } from 'react-i18next'
import {
  ControlProps,
  DropdownIndicatorProps,
  GroupBase,
  OptionProps,
  SingleValueProps,
  StylesConfig,
  components,
} from 'react-select'
import { RootStoreContext } from '@stores/rootStore'
import { BsDot } from '@react-icons/all-files/bs/BsDot'
import { LFNumber } from '@components/LFNumber'

export interface IBaseOption {
  label: string
  value: string | number | null | undefined
  isDisabled?: boolean
}

export const DefaultSingleValue = observer(
  <T extends IBaseOption, IsMulti extends boolean = false>(
    props: SingleValueProps<T, IsMulti, GroupBase<T>>
  ) => {
    const { t } = useTranslation()
    return (
      <components.SingleValue {...props}>
        <p className="truncate">{t(props.data.label)}</p>
      </components.SingleValue>
    )
  }
)

export const DropdownIndicator = <
  T extends IBaseOption,
  IsMulti extends boolean = false
>(
  props: DropdownIndicatorProps<T, IsMulti, GroupBase<T>>
) => (
  <components.DropdownIndicator {...props}>
    <DropdownCaret className="w-6 text-grey" />
  </components.DropdownIndicator>
)

export const Menu = observer((props) => (
  <components.Menu {...props}>
    {props.children}
    {props.isLoading && <Spinner isLoading />}
  </components.Menu>
))

export const ControlComponent = observer(
  <T extends IBaseOption, IsMulti extends boolean = false>(
    props: ControlProps<T, IsMulti, GroupBase<T>> & { padding?: string }
  ) => {
    const { t } = useTranslation()
    return (
      <label
        className={`
          ${
            props.selectProps.label
              ? `
                rounded-lg
                md:border
              `
              : ''
          }
          h-full
          ${props.isFocused ? 'border-secondary-300' : 'border-silver'}
          flex flex-col text-xl text-grey
          md:flex-row
        `}
      >
        <div
          className={`
            flex font-thin capitalize
            md:hidden
          `}
        >
          {t(props.selectProps?.label as string)}
          {props.selectProps?.isRequired && props?.selectProps?.label && (
            <span className="pl-1 text-2xl text-negative-300">*</span>
          )}
        </div>
        {props.selectProps.label ? (
          <div
            className={`
              w-max-1/3 hidden min-h-16 min-w-min rounded-l-lg px-4
              md:block
              ${props.isDisabled ? 'bg-silver' : 'bg-spindle'}
            `}
          >
            <div className="flex h-full items-center">
              <p className="truncate font-thin text-grey capitalize">
                {t(props?.selectProps?.label)}
              </p>
              {props.selectProps?.isRequired && props?.selectProps?.label && (
                <span className="pl-1 text-2xl text-negative-300">*</span>
              )}
            </div>
          </div>
        ) : (
          ''
        )}
        <div
          className={`
            w-full
            ${props.selectProps.label ? 'border' : 'border-none'}
            md:border-none
            ${props.isFocused ? 'border-secondary-300' : 'border-silver'}
            ${
              props.isDisabled
                ? `
                  bg-silver
                  md:bg-none
                `
                : 'bg-none'
            }
            overflow-hidden rounded-lg
            md:rounded-none md:rounded-r-lg
          `}
        >
          <components.Control {...props} />
        </div>
      </label>
    )
  }
)

export const generateDefaultDropdownStyles = <
  T extends IBaseOption,
  IsMulti extends boolean = false
>(): StylesConfig<T, IsMulti, GroupBase<T>> => ({
  control: (provided, { isDisabled, selectProps: { textTransform } }) => ({
    ...provided,
    border: 0,
    height: '100%',
    background: isDisabled ? '#E5E9F3' : 'transparent',
    boxShadow: 'none',
    borderRadius: 0,
    cursor: 'pointer',
    ...(isDisabled && {
      pointerEvents: 'auto',
      cursor: 'not-allowed',
    }),
    textTransform: textTransform,
  }),
  container: (provided) => ({
    ...provided,
    height: '100%',
    background: 'transparent',
    borderRadius: '0.375rem',
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    fontSize: '1.6rem',
    color: '#1C1F28',
    textTransform: state.selectProps.textTransform,
    background: state.isDisabled ? '#E5E9F3' : 'transparent',
  }),
  indicatorsContainer: (provided, state) => ({
    ...provided,
    marginTop: 0,
    marginRight: -5,
    background: state.isDisabled ? '#E5E9F3' : 'transparent',
  }),
  singleValue: (provided, state) => ({
    ...provided,
    textTransform: state.selectProps.textTransform,
  }),
  menu: (provided, state) => ({
    ...provided,
    textTransform: state.selectProps.textTransform,
    zIndex: '1000',
    border: '#BFC7E1',
    padding: 10,
    fontWeight: 300,
    cursor: state.selectProps.isDisabled ? 'cursor-not-allowed' : 'pointer',
  }),
  option: (provided, state) => ({
    ...provided,
    color: state.isSelected ? '#084C83' : '#727D9F',
    background: state.isSelected ? '#F7F8FB' : 'inherit',
    '&:hover': {
      background: '#F7F8FB',
    },
    fontWeight: state.isSelected ? '500' : '300',
    cursor: 'pointer',
    fontSize: '1.6rem',
    ...(state.isDisabled && {
      pointerEvents: 'auto',
      cursor: 'not-allowed',
    }),
  }),
  input: (provided) => ({
    ...provided,
    fontSize: '1.6rem',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#BFC7E1',
    fontSize: '1.6rem',
  }),
})

export const defaultNumberDropdownStyles = {
  control: (provided) => ({
    ...provided,
    border: 0,
    minHeight: 0,
    background: 'transparent',
    boxShadow: 'none',
    cursor: 'pointer',
  }),
  valueContainer: (provided) => ({
    ...provided,
    fontSize: '1.25rem',
    border: 'none',
    color: '#1C1F28',
    padding: 0,
    paddingLeft: 8,
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    marginTop: 0,
    width: 'fit',
  }),
  singleValue: (provided) => ({
    ...provided,
    display: 'contents', // ensures value container takes height of component but confuses input
  }),
  option: (provided, state) => ({
    ...provided,
    color: state.isSelected ? '#084C83' : '#727D9F',
    background: state.isSelected ? '#F7F8FB' : 'inherit',
    '&:hover': {
      background: '#F7F8FB',
    },
    fontWeight: state.isSelected ? '500' : '300',
    fontSize: '1.6rem',
  }),
  input: (provided) => ({
    ...provided,
    marginLeft: -2,
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: '1.6rem',
  }),
}

export interface ICropzoneOption extends IBaseOption {
  cropzoneLabel: string
  cropName: string
  cropzoneArea: number
  value: string
  farmId: string
}

const CropzoneTemplate: FC<{
  data: OptionProps<ICropzoneOption, true, GroupBase<ICropzoneOption>>['data']
  className?: string
}> = observer(({ data, className }) => {
  const { userStore } = useContext(RootStoreContext)
  const { t } = useTranslation()
  const unit = userStore.user.settings.area_unit

  return (
    <div {...(className && { className: className })}>
      <div className="flex flex-row items-center gap-x-2 overflow-x-hidden">
        <p className="mb-1 truncate text-xl text-grey"> {data.cropzoneLabel}</p>
        {data.cropzoneArea && (
          <div
            className={`
              hidden
              xl:flex
            `}
          >
            <BsDot className="text-grey" />
            <div className="text-lg">
              <LFNumber
                num={data.cropzoneArea}
                unit={t(unit)}
                format="area"
                computed
              />
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-row items-center gap-x-2 overflow-x-hidden">
        <CropIcon
          crop={data.cropName}
          className={`
            h-8
            lg:h-10
          `}
        />

        <p className="ml-2 truncate text-black">{data.label}</p>
      </div>
    </div>
  )
})

export const CropzoneOption = observer(
  <T extends ICropzoneOption, IsMulti extends boolean = false>(
    props: OptionProps<T, IsMulti, GroupBase<T>>
  ) => (
    <components.Option {...props}>
      <CropzoneTemplate data={props.data} className="pb-2" />
    </components.Option>
  )
)

export const CropzoneSingleValue = observer(
  <T extends ICropzoneOption, IsMulti extends boolean = false>(
    props: SingleValueProps<T, IsMulti, GroupBase<T>>
  ) => (
    <components.SingleValue {...props}>
      <CropzoneTemplate data={props.data} />
    </components.SingleValue>
  )
)
